/*
 * FleetLocationView — standalone, experimental Vehicle Location map (Scania POC).
 *
 * Isolated on purpose: it is NOT linked from existing fleet navigation. Mount it
 * behind the feature flag by adding ONE route to the router, e.g. (react-router v5):
 *
 *     import FleetLocationView from './components/FleetLocationView/FleetLocationView';
 *     <Route path="/fleet-location/:vehicleId" component={FleetLocationView} />
 *
 * The route is only reachable when the backend reports fleetLocationEnabled=true
 * (checked via /uiapi/1/fleetlocation/config/). The component loads the HERE Maps
 * JavaScript SDK from the HERE CDN on demand.
 *
 * The pinned asset list and its integrity hashes live in hereSdk.js, shared with the fleet
 * map and asserted against the design by fleetlocation/tests/test_frontend_template.py.
 * Also ensure the CSP (if any) allows js.api.here.com.
 */

import React from 'react';
import { translate } from 'react-i18next';

import BackButton from '../shared/BackButton';
import CBMediaQuery from '../shared/CBMediaQuery';
import { getCsrfToken, getStyle } from '../../utils/utils';
import { fleetLocationStyles as styles } from './fleetLocationStyles';

import { loadHereSdk } from './hereSdk';
import VehicleHistoryPanel from './VehicleHistoryPanel';
import InlineNameEditor from './InlineNameEditor';
import { labelFor } from './fleetMapCore';

class FleetLocationView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      status: 'loading',
      message: '',
      position: null,
      stale: false,
      vehicle: null,
      retentionMonths: null,
      mapUnavailable: false,
      loadedVehicleId: null,
    };
    this.mapRef = React.createRef();
    this.historyRef = React.createRef();
    this.map = null;
    this.bootId = 0;
    this.mounted = false;
    this.controller = null;
  }

  componentDidMount() {
    this.mounted = true;
    this.boot();
  }

  componentDidUpdate(prevProps) {
    if (this.vehicleId(prevProps) !== this.vehicleId()) {
      this.boot();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.bootId += 1;
    if (this.controller) { this.controller.abort(); }
    this.disposeMap();
  }

  disposeMap() {
    const history = this.historyRef.current;
    if (history && history.clear) { history.clear(); }
    if (this.map) {
      this.map.dispose();
      this.map = null;
    }
  }

  vehicleId(props = this.props) {
    // react-router v5 passes params via this.props.match.params.
    if (props.match && props.match.params) {
      return props.match.params.vehicleId;
    }
    return props.vehicleId;
  }

  async boot() {
    const generation = this.bootId + 1;
    this.bootId = generation;
    if (this.controller) { this.controller.abort(); }
    this.controller = typeof AbortController === 'undefined' ? null : new AbortController();
    const signal = this.controller && this.controller.signal;
    const current = () => this.mounted && this.bootId === generation;
    const id = this.vehicleId();
    this.disposeMap();
    this.setState({
      status: 'loading', message: '', vehicle: null, position: null,
      stale: false, retentionMonths: null, mapUnavailable: false, loadedVehicleId: null,
    });
    try {
      const config = await this.getJson('/uiapi/1/fleetlocation/config/', signal);
      if (!current()) { return; }
      if (!config.fleetLocationEnabled) {
        this.setState({ status: 'disabled', message: this.props.t('notAvailable') });
        return;
      }
      let mapUnavailable = !config.hereApiKey;
      if (!mapUnavailable) {
        try {
          await loadHereSdk();
        } catch (error) {
          mapUnavailable = true;
        }
      }
      if (!current()) { return; }
      let data;
      let currentUnavailable = false;
      try {
        data = await this.getJson(
          '/uiapi/1/fleetlocation/vehicle/' + id + '/current/', signal
        );
      } catch (error) {
        // Only the authorized current endpoint's matching identity unlocks the history
        // shell during a provider outage. Authentication/404 errors remain hard gates.
        const failed = error.data;
        if ((error.status === 502 || error.status === 503) && failed && failed.vehicle
          && String(failed.vehicle.id) === String(id)) {
          data = failed;
          currentUnavailable = true;
        } else {
          throw error;
        }
      }
      if (!current()) { return; }
      if (!mapUnavailable) {
        try {
          this.initMap(config.hereApiKey, data.position || null, data.stale);
          mapUnavailable = !this.map;
        } catch (error) {
          this.disposeMap();
          mapUnavailable = true;
        }
      }
      if (currentUnavailable || data.status === 'no_recent_position' || !data.position) {
        this.setState({
          status: currentUnavailable ? 'current_unavailable' : 'no_recent',
          message: this.props.t(currentUnavailable
            ? 'currentPositionUnavailable' : 'noRecentPositionForVehicle'),
          vehicle: data.vehicle || null,
          retentionMonths: data.retentionMonths,
          mapUnavailable,
          loadedVehicleId: id,
        });
        return;
      }
      this.setState({
        status: 'ok',
        position: data.position,
        stale: !!data.stale,
        vehicle: data.vehicle || null,
        retentionMonths: data.retentionMonths,
        mapUnavailable,
        loadedVehicleId: id,
      });
    } catch (err) {
      if (!current()) { return; }
      this.setState({ status: 'error', message: this.props.t('couldNotLoadVehicle') });
    }
  }

  async getJson(url, signal) {
    const response = await fetch(url, {
      credentials: 'include', headers: { Accept: 'application/json' },
      ...(signal ? { signal } : {}),
    });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error('http ' + response.status);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  }

  async saveName(friendlyName, signal) {
    const id = this.vehicleId();
    const generation = this.bootId;
    const response = await fetch('/uiapi/1/fleetlocation/vehicle/' + id + '/name/', {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken() },
      body: JSON.stringify({ friendly_name: friendlyName }),
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) { throw new Error('nameSaveFailed'); }
    const data = await response.json();
    if (!data.vehicle || String(data.vehicle.id) !== String(id)) { throw new Error('nameSaveFailed'); }
    if (!this.mounted || this.bootId !== generation || (signal && signal.aborted)) { return; }
    this.setState({ vehicle: data.vehicle });
  }

  initMap(apiKey, position, stale) {
    const H = window.H;
    if (!H || !this.mapRef.current) {
      return;
    }
    const center = position
      ? { lat: position.lat, lng: position.lng }
      : { lat: 60.1699, lng: 24.9384 }; // Helsinki fallback
    const platform = new H.service.Platform({ apiKey });
    const layers = platform.createDefaultLayers();
    this.map = new H.Map(this.mapRef.current, layers.vector.normal.map, {
      center,
      zoom: position ? 13 : 6,
      pixelRatio: window.devicePixelRatio || 1,
    });
    new H.mapevents.Behavior(new H.mapevents.MapEvents(this.map));
    H.ui.UI.createDefault(this.map, layers);
    if (position) {
      // A STALE FIX MUST NOT LOOK CURRENT. The backend returns an old position on
      // purpose -- "here is where it was, and it is old" beats "no idea" -- so the marker
      // has to carry that distinction or the API's honesty is undone in the last 20 pixels.
      //
      // An explicit grey icon rather than a marker opacity option: opacity is not part of
      // the documented H.map.Marker options, so it would either work or be silently
      // ignored, and "silently ignored" is the failure this whole branch exists to avoid.
      if (stale) {
        const greyPin =
          '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="34" viewBox="0 0 24 34">'
          + '<path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 22 12 22s12-13 12-22c0-6.6-5.4-12-12-12z" '
          + 'fill="#9e9e9e" stroke="#616161" stroke-width="1.5"/>'
          + '<circle cx="12" cy="12" r="4.5" fill="#f5f5f5"/></svg>';
        this.map.addObject(
          new H.map.Marker(center, { icon: new H.map.Icon(greyPin, { size: { w: 24, h: 34 } }) })
        );
      } else {
        this.map.addObject(new H.map.Marker(center));
      }
    }
  }

  render() {
    const { status, message, position, stale, vehicle } = this.state;
    const { t } = this.props;
    const vehicleLabel = vehicle ? labelFor(vehicle, t('vehicleFallback', { id: vehicle.id })) : '';
    const historyAvailable = status === 'ok' || status === 'no_recent'
      || status === 'current_unavailable';
    // The badge is the other half of returning a stale fix at all: without it an old
    // position is indistinguishable from a current one, which is the single wrong answer
    // this feature can give a fleet operator.
    // The RAW timestamp, handed to i18next to format in the selected language. Formatting it
    // here would follow the BROWSER's language instead, and put an American date inside a
    // Finnish sentence on a laptop set to US English.
    const lastSeen = position && position.ts ? position.ts : null;
    return (
      <CBMediaQuery>
        {(screenSize) => (
          <div style={getStyle(screenSize, styles, 'container')}>
            {/* A DEAD END OTHERWISE. This screen is reached from a pin or a table row on
                the fleet map, and had no way back except the browser button. */}
            <BackButton to='/fleet-map' />
            <h2 style={getStyle(screenSize, styles, 'heading')}>{t('vehicleLocationTitle')}</h2>
            {/*
              WHICH VEHICLE. A dot on a map that names no vehicle cannot be checked by the
              person looking at it -- they cannot tell whether it is the right one, or which
              of a fleet it is. The identity comes from the same response as the position, so
              it can never describe a different vehicle than the pin.
            */}
            {vehicle && (
              <p style={getStyle(screenSize, styles, 'vehicleName')}>
                {vehicle.can_rename === true ? (
                  <InlineNameEditor key={this.bootId + ':' + vehicle.id} value={vehicle.friendly_name}
                    display={vehicleLabel} contextLabel={vehicleLabel} t={t} onSave={(value, signal) => this.saveName(value, signal)} />
                ) : vehicleLabel}
                {(vehicle.manufacturer || vehicle.model_name) && (
                  <span style={getStyle(screenSize, styles, 'vehicleModel')}>
                    {' — '}
                    {[vehicle.manufacturer, vehicle.model_name].filter(Boolean).join(' ')}
                  </span>
                )}
              </p>
            )}
            {vehicle && (vehicle.name || vehicle.registration_number) && vehicle.vin && (
              <p style={getStyle(screenSize, styles, 'vin')}>
                {t('vinPrefix', { vin: vehicle.vin })}
              </p>
            )}
            {status === 'loading' && (
              <p style={getStyle(screenSize, styles, 'summary')}>{t('loading')}</p>
            )}
            {(status === 'disabled' || status === 'error' || status === 'no_recent'
              || status === 'current_unavailable') && (
              <p style={getStyle(screenSize, styles, 'summary')}>{message}</p>
            )}
            {historyAvailable && this.state.mapUnavailable && (
              <p style={getStyle(screenSize, styles, 'summary')}>{t('mapUnavailable')}</p>
            )}
            {status === 'ok' && stale && lastSeen && (
              <p style={getStyle(screenSize, styles, 'staleBanner')}>
                {t('positionOutOfDate', { time: lastSeen })}
              </p>
            )}
            {status === 'ok' && !stale && lastSeen && (
              <p style={getStyle(screenSize, styles, 'summary')}>
                {t('positionAsOf', { time: lastSeen })}
              </p>
            )}
            {/* SERVICE INTERVALS ARE KEYED TO KILOMETRES, NOT TO DATES, so this is the
                number a manager forecasting the next service is actually after. Shown
                whether or not the fix is current: an odometer does not go stale the way a
                position does -- a truck parked for a week has travelled nothing since.
                Rounded to whole kilometres; the two decimals the panel uses are for
                subtracted trip distances, and no schedule is written to thirty metres.

                THE PARKED TRUCK IS NOT THE ONLY TRUCK, though, which is why the reading
                names its own moment when that moment is not the fix's. Position and
                odometer arrive in separate messages and only about half carry a reading,
                so a vehicle that drove for three hours reporting positions and no odometer
                shows a total that understates by however far it went. The test is exact
                equality against the fix's timestamp, not a staleness bound: any bound
                would be a guess about a fleet this component cannot see. Expect the
                as-of line OFTEN rather than rarely -- roughly half the fixes carry no
                reading -- so a routinely visible one is the design working, not a bug
                inviting someone to add the threshold this deliberately refused. */}
            {status === 'ok' && position && position.odometer_km !== null
              && position.odometer_km !== undefined && (
              <p style={getStyle(screenSize, styles, 'summary')}>
                {position.odometer_ts && position.odometer_ts !== position.ts
                  ? t('odometerTotalAsOf', {
                    km: Math.round(position.odometer_km),
                    time: position.odometer_ts,
                  })
                  : t('odometerTotal', { km: Math.round(position.odometer_km) })}
              </p>
            )}
            <div
              ref={this.mapRef}
              style={{
                ...getStyle(screenSize, styles, 'map'),
                display: status === 'disabled' || this.state.mapUnavailable ? 'none' : 'block',
              }}
            />
            {/* History uses this map when available. A missing map or current position does not
                hide history; the loaded vehicle identity must still match the route. */}
            {historyAvailable && this.state.loadedVehicleId === this.vehicleId() && (
              <VehicleHistoryPanel
                ref={this.historyRef}
                key={this.vehicleId()}
                map={this.map}
                vehicleId={this.vehicleId()}
                t={t}
                i18n={this.props.i18n}
                screenSize={screenSize}
                retentionMonths={this.state.retentionMonths}
              />
            )}
          </div>
        )}
      </CBMediaQuery>
    );
  }
}

export default translate('FleetLocation')(FleetLocationView);
